/**
 * The two unauthenticated routes, declared as data so that whoever owns the cabinet's router can
 * mount them without importing anything else from this tree.
 *
 * WHY DESCRIPTORS RATHER THAN A ROUTE TREE. When this file was written TanStack Router (ruled by
 * adr/0007-web-app-stack.md) was not installed, and a `{ path, element }` pair was the smallest
 * thing that would survive whichever router landed. It has landed: `src/router.tsx` turns each
 * entry below into a `createRoute`, and `test/router.spec.ts` asserts both paths reach it.
 *
 * `/enter` IS FIXED BY THE CORPUS (adr/0007). `/sign-in` is ours — docs/ui/04-pages.md marks
 * its row "ruled", which overstates what ADR-0007 says.
 *
 * BOTH ARE UNAUTHENTICATED, and they are the only two that are. Anything that renders behind a
 * session belongs to another route tree.
 */
import type { ReactElement } from 'react';
import { EnterRoute } from './enter';
import { SignInRoute } from './sign-in';
import { ENTER_PATH, SIGN_IN_PATH } from '../../features/auth/navigation';
import type { Navigation } from '../../features/auth/navigation';

/** What the router hands a mounted auth route. Both are optional so a bare render still works. */
export interface AuthRouteElementProps {
  /** Called once the person is signed in and their session has loaded. */
  readonly onSignedIn?: () => void;
  /** The router's history as a `Navigation`; the screens fall back to the browser's own. */
  readonly navigation?: Navigation;
}

export interface AuthRouteDescriptor {
  readonly path: string;
  /** Both routes are reachable with no session. Stated so a guard cannot be added by accident. */
  readonly requiresSession: false;
  readonly element: (props: AuthRouteElementProps) => ReactElement;
}

export const authRoutes: readonly AuthRouteDescriptor[] = [
  {
    path: SIGN_IN_PATH,
    requiresSession: false,
    element: ({ onSignedIn, navigation }) => (
      <SignInRoute
        {...(onSignedIn === undefined ? {} : { onSignedIn })}
        {...(navigation === undefined ? {} : { navigation })}
      />
    ),
  },
  {
    path: ENTER_PATH,
    requiresSession: false,
    element: ({ onSignedIn, navigation }) => (
      <EnterRoute
        {...(onSignedIn === undefined ? {} : { onSignedIn })}
        {...(navigation === undefined ? {} : { navigation })}
      />
    ),
  },
];

export { EnterRoute, SignInRoute };
// Not routes: where a door lands (R-034) and the root's not-found (R-043) - both need no session.
export { isKnownPath, landingPathAmong } from './landing';
export { CabinetNotFound } from './not-found';
export type { EnterRouteProps } from './enter';
export type { SignInRouteProps } from './sign-in';
